import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { compose, graphql, MutationFunc } from 'react-apollo';

import { getSize, getAttributeDefinitions, displayAttributeDefinitions } from '../helpers/products';
import FilterableAttributes from '../components/Query/Attributes/FilterableAttributes';
import Order from '../components/Query/Orders/Order';
import UserWrap from '../components/UserWrap';
import { areAddressesSame } from '../helpers/areAddressesSame';
import { getClassForStatus } from '../helpers/orders/getClassForStatus';
import { Loader, ProductBox, Section, Title } from '../modules/modules';
import { CurrencyFormatter as Currency } from '../utils/utils';
import Image from '../components/Image';
import CountryService from '../services/CountryService';
import VatService from '../services/VatService';
import { getOrderVat } from '../helpers/orders/getOrderVat';
import { CreatePaymentMutation } from '../components/Query/Payment/CreatePayment';
import OrderPayButton from '../components/OrderPayButton';
import { getPriceWithoutVat } from 'data-layer/src/helpers/prices/getPriceWithoutVat';
import { vatDescription } from '../helpers/vatDescription';

interface IOrderDetailProps extends RouteComponentProps<{
  orderNumber: string;
  language: string;
}> {
  createPayment: MutationFunc;
}

interface IOrderDetailState {
  executedPaymentProcess: boolean;
}

class OrderDetail extends React.Component<IOrderDetailProps, IOrderDetailState> {

  public constructor(props: IOrderDetailProps) {
    super(props);

    this.state = {
      executedPaymentProcess: false,
    };
  }

  public render() {
    const { language } = CountryService.GetInstance();

    return (
      <main>
        <Section type="userPage">
          <UserWrap>
            <Order orderNumber={this.props.match.params.orderNumber}>
              {({ order }: any) => {
                return (
                  <FilterableAttributes>
                    {({ attributes }: any) => {
                      if (order.loading || !order.order || attributes.loading) {
                        return <Loader />;
                      }

                      const currency = order.order.currency;

                      const country = order.order.country;

                      const o = order.order as GQL.IOrder;

                      // TODO: no fuj !!!
                      if (o === undefined) {
                        return <div />;
                      }

                      return (
                        <div>
                          <div className="orderDetail">
                            <Title type="lower">Order No. {o.orderNumber}</Title>

                            <div className="orderDetail__grid">
                              <div className="row">
                                <div className="col-md-4">
                                  <b>Created at:</b>
                                  <div className="space-between">
                                    <p>{new Date(o.createdAt).toLocaleDateString()}</p>
                                  </div>
                                  {o.externalState &&
                                    <React.Fragment>
                                      <b>Status:</b>
                                      <p className={getClassForStatus(o.externalState)}>
                                        {o.externalState.name[language]}
                                      </p>
                                    </React.Fragment>}
                                  {o.paid && o.paidAt &&
                                    <React.Fragment>
                                      <b>Paid at:</b>
                                      <p>{new Date(o.paidAt).toLocaleDateString()} {new Date(o.paidAt).toLocaleTimeString()}</p>
                                    </React.Fragment>}
                                </div>

                                <div className="col-md-4">
                                  <b>Billing information:</b>
                                  <p>
                                    {o.invoiceFirstName} {o.invoiceLastName}<br />
                                    {o.invoiceAddressLine1}<br />
                                    {o.invoiceAddressLine2 &&
                                      <React.Fragment>
                                        {o.invoiceAddressLine2}<br />
                                      </React.Fragment>}
                                    {o.invoiceCity}<br />
                                    {o.invoiceZip}<br />
                                    {o.invoiceCountyState &&
                                      <React.Fragment>
                                        {o.invoiceCountyState}<br />
                                      </React.Fragment>}
                                    {o.invoiceCountry}
                                  </p>
                                </div>

                                <div className="col-md-4">
                                  <b>Shipping information:</b>
                                  {o.anotherDeliveryAddress &&
                                    <p>
                                      {o.deliveryFirstName} {o.deliveryLastName}<br />
                                      {o.deliveryAddressLine1}<br />
                                      {o.deliveryAddressLine2 &&
                                      <React.Fragment>
                                        {o.deliveryAddressLine2}<br />
                                      </React.Fragment>}
                                      {o.deliveryCity}<br />
                                      {o.deliveryZip}<br />
                                      {o.deliveryCountyState &&
                                        <React.Fragment>
                                          {o.deliveryCountyState}<br />
                                        </React.Fragment>}
                                      {o.deliveryCountry}
                                    </p>}
                                  {!o.anotherDeliveryAddress && 
                                    <p>Same as billing information</p>}
                                </div>
                              </div>
                            </div>

                            <div className="cartProducts">
                              <div className="visible-md">
                                <table className="table table--orderDetail bordered mt-3">
                                  <thead>
                                    <tr>
                                      <th colSpan={2}>Product</th>
                                      <th className="cartProduct__price text-center">Price {VatService.GetInstance().displayIncludeVat(getOrderVat(order.order), country)}</th>
                                      <th className="cartProduct__quantity text-center">Quantity</th>
                                      <th className="cartProduct__price text-center">Total price</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {o.orderedItems.map(item => {
                                      const variantDefinitions = getAttributeDefinitions(
                                        item,
                                        attributes.attributes,
                                        o.language
                                      );

                                      return (
                                        <tr key={item.id} className="cartProduct">
                                          <td className="cartProduct__image">
                                            <div className="cartProduct__preview">
                                              <Image image={item.image} width={190} height={190} alt={item.name[o.language]} />
                                            </div>
                                          </td>
                                          <td className="cartProduct__title">
                                            {item.name[o.language]}
                                            <div className="cartProduct__code">
                                              Item number: #{item.ean}
                                            </div>
                                            {variantDefinitions &&
                                              <div className="cartProduct__definitions">
                                                {displayAttributeDefinitions(variantDefinitions)}
                                              </div>}
                                          </td>
                                          <td className="cartProduct__price text-right">
                                            <div>
                                              <Currency
                                                  price={getPriceWithoutVat(item.price, item.VATrate)}
                                                  decimals={2}
                                                  currency={currency}
                                                />
                                            </div>
                                            {item.VATrate > 0 &&
                                              <span className="withoutVat">
                                                <Currency price={item.price} currency={currency} /> incl. {vatDescription()}
                                              </span>}
                                          </td>
                                          <td className="cartProduct__quantity">
                                            <strong>{item.quantity}</strong> pcs
                                          </td>
                                          <td className="cartProduct__price text-bold text-right">
                                            <div>
                                              <Currency
                                                  price={getPriceWithoutVat(item.price, item.VATrate) * item.quantity}
                                                  decimals={2}
                                                  currency={currency}
                                                />
                                            </div>
                                            {item.VATrate > 0 &&
                                              <span className="withoutVat">
                                                <Currency price={item.price * item.quantity} currency={currency} /> incl. {vatDescription()}
                                              </span>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              <div className="hidden-md">
                                <table className="table table--orderDetail bordered mt-3">
                                  <thead>
                                    <tr>
                                      <th>Product</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {o.orderedItems.map(item => {
                                      const size = getSize(
                                        item,
                                        attributes.attributes.filter((a: any) => a.tags.includes('size')),
                                        o.language
                                      );
                                      return (
                                        <tr key={item.id} className="cartProduct">
                                          <td>
                                            <ProductBox
                                              orderItem={item}
                                              // variant={
                                              //   {
                                              //     id: item.id,
                                              //     definition: item.definition,
                                              //     product: {
                                              //       name: item.name,
                                              //       price: item.price,
                                              //       images: []
                                              //     }
                                              //   } as any
                                              // }
                                              image={item.image}
                                              size={size && size.toString()}
                                              quantity={item.quantity}
                                              boxType="order"
                                              currency={currency}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              <table className="table table--orderDetail bordered">
                                <tbody>
                                  {(o.tariffTotal && <tr>
                                    <td className="cartProduct__title">
                                      <div className="cartProduct__textItem">
                                        U.S. Reciprocal Import Tariff
                                      </div>
                                    </td>
                                    <td className="cartProduct__price cartProduct__deliveryPrice text-bold">
                                      {o.deliveryTotal === null &&
                                        <span>{o.deliveryMethod.unknownPriceLabel[language]}</span>}
                                      {o.deliveryTotal !== null &&
                                        <Currency price={o.tariffTotal} currency={currency} />}
                                    </td>
                                  </tr>)}
                                  <tr>
                                    <td className="cartProduct__title">
                                      <div className="cartProduct__textItem">
                                        Shipping - {o.deliveryMethod.name[o.language]}
                                      </div>
                                    </td>
                                    <td className="cartProduct__price cartProduct__deliveryPrice text-bold">
                                      {o.deliveryTotal === null &&
                                        <span>{o.deliveryMethod.unknownPriceLabel[language]}</span>}
                                      {o.deliveryTotal !== null &&
                                        <Currency price={o.deliveryTotal} currency={currency} />}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="cartProduct__title">
                                      <div className="cartProduct__textItem">
                                        Payment - {o.paymentMethod.name[o.language]}
                                      </div>
                                    </td>
                                    <td className="cartProduct__price cartProduct__paymentPrice text-bold">
                                      <Currency price={o.paymentTotal} currency={currency} />
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          
                            {o.paymentMethod && o.paymentMethod.gateway && !o.paid &&
                              <div className="payOrder">
                                <OrderPayButton
                                  order={o}
                                  onClick={() => this.executePaymentProcess(o)}
                                  onRefetch={order.refetch}
                                  handleCreatePayment={this.props.createPayment}
                                />
                              </div>}
                          </div>
                        </div>
                      );
                    }}
                  </FilterableAttributes>
                );
              }}
            </Order>
          </UserWrap>
        </Section>
      </main>
    );
  }

  private sameAddresses(order: GQL.IOrder) {
    return areAddressesSame(
      {
        city: order.invoiceCity,
        country: order.invoiceCountry,
        houseNumber: order.invoiceHouseNumber,
        street: order.invoiceStreet,
        zip: order.invoiceZip
      } as any,
      {
        city: order.deliveryCity,
        country: order.deliveryCountry,
        houseNumber: order.deliveryHouseNumber,
        street: order.deliveryStreet,
        zip: order.deliveryZip
      } as any
    );
  }

  /**
   * Založení platby
   * 
   * @param order 
   */
  private async executePaymentProcess(order: any) {

    // todo: pokud je povolen příjem chyby, celé se to zacyklí
    // todo: nutno přidat ošetření aby se tato fce pouštěla jen 1x !!!!
    if (this.state.executedPaymentProcess === false) {
      this.setState({
        executedPaymentProcess: true,
      }, async () => {

        // vytvoření platby
        const createPaymentResult = await this.props.createPayment({
          variables: {
            orderNumber: order.orderNumber,
            language: order.language,
            paymentMethod: order.paymentMethod.id,
          }
        });
    
        // chyba platby
        if (createPaymentResult.data
          && createPaymentResult.data.createPayment
          && createPaymentResult.data.createPayment.error
        ) {
          // alert(createPaymentResult.data.createPayment.error);
          // todo: report to FoxMedia
        }
    
        // todo: processing dle platební brány
        if (createPaymentResult.data
          && createPaymentResult.data.createPayment
          && createPaymentResult.data.createPayment.gatewayUrl
        ) {
          window.location.href = createPaymentResult.data.createPayment.gatewayUrl;
        }
      });
    }
  }
}

export default compose(
  graphql<any, any>(CreatePaymentMutation, {
    name: 'createPayment'
  })
)(OrderDetail);



// WEBPACK FOOTER //
// ./src/pages/OrderDetail.tsx